import { useState, useRef } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Upload, Image as ImageIcon, Loader2, Copy, Check, Trash2, ExternalLink } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminMedia = () => {
  const { token } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = async (file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file (JPG, PNG, WebP, etc.)");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image size must be less than 10MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${BACKEND_URL}/api/upload-image`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data?.url) {
        setUploads((prev) => [
          {
            url: response.data.url,
            filename: response.data.filename || file.name,
            uploadedAt: new Date().toLocaleString(),
            size: (file.size / 1024).toFixed(1) + " KB",
          },
          ...prev,
        ]);
        toast.success("Image uploaded successfully");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleRemove = (index) => {
    setUploads((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-media-page">
        <div>
          <h1 className="text-3xl font-semibold text-foreground mb-2">Media Uploads</h1>
          <p className="text-muted-foreground">Upload images for OG tags, blog posts, or any other purpose</p>
        </div>

        {/* Upload Area */}
        <div className="bg-white rounded-xl p-6 shadow-soft">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Upload className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Upload Image</h2>
              <p className="text-sm text-muted-foreground">Drag & drop or click to upload. Max 10MB, images only.</p>
            </div>
          </div>

          <div
            className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all ${
              dragActive
                ? "border-blue-400 bg-blue-50"
                : "border-gray-200 hover:border-gray-400 hover:bg-gray-50"
            }`}
            onClick={() => fileInputRef.current?.click()}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            data-testid="media-upload-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
              data-testid="media-upload-input"
            />
            {uploading ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
                <p className="text-sm text-gray-600">Uploading...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
                  <ImageIcon className="w-7 h-7 text-gray-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    Drop an image here or <span className="text-blue-600 underline">browse</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-1">JPG, PNG, WebP up to 10MB</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Uploaded Files */}
        {uploads.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-soft">
            <h2 className="text-lg font-semibold text-foreground mb-4">
              Uploaded Images ({uploads.length})
            </h2>
            <div className="space-y-4">
              {uploads.map((item, index) => (
                <UploadedItem key={index} item={item} onRemove={() => handleRemove(index)} />
              ))}
            </div>
          </div>
        )}

        {/* Info */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-sm text-amber-800">
            <strong>Tip:</strong> Uploaded images are stored permanently on cloud storage (Cloudflare R2).
            Copy the URL and paste it wherever needed — OG image fields, blog content, etc.
          </p>
        </div>
      </div>
    </AdminLayout>
  );
};

const UploadedItem = ({ item, onRemove }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(item.url);
      setCopied(true);
      toast.success("URL copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  return (
    <div className="flex items-center gap-4 p-3 rounded-lg border border-gray-100 bg-gray-50" data-testid="uploaded-media-item">
      {/* Preview */}
      <img
        src={item.url}
        alt={item.filename}
        className="w-16 h-16 object-cover rounded-lg border flex-shrink-0"
        onError={(e) => { e.target.style.display = "none"; }}
      />

      {/* Info + URL */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800 truncate">{item.filename}</p>
        <div className="flex items-center gap-2 mt-1">
          <Input
            value={item.url}
            readOnly
            className="text-xs h-8 bg-white font-mono"
            data-testid="uploaded-media-url"
          />
        </div>
        <p className="text-xs text-gray-400 mt-1">{item.size} &middot; {item.uploadedAt}</p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button
          variant="outline"
          size="sm"
          onClick={handleCopy}
          className="h-8 px-2"
          data-testid="copy-media-url-btn"
        >
          {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
        </Button>
        <a href={item.url} target="_blank" rel="noopener noreferrer">
          <Button variant="outline" size="sm" className="h-8 px-2" data-testid="open-media-url-btn">
            <ExternalLink className="w-4 h-4" />
          </Button>
        </a>
        <Button
          variant="outline"
          size="sm"
          onClick={onRemove}
          className="h-8 px-2 text-red-500 hover:text-red-600"
          data-testid="remove-media-item-btn"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default AdminMedia;
